import { useEffect, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { OnDragEndResponder, OnDragStartResponder } from '@hello-pangea/dnd';

import Chapter from '@/models/Chapter';
import ConditionalRenderer from '../../../ConditionalRenderer';
import MainButton from '../../../buttons/MainButton';
import { DragDropItem, DragDropList } from '../../../dataInput/DragDrop';
import { dropHandler } from '@/utils/dragdrop';
import Subchapter from '@/models/Subchapter';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import { StudentBookColorIcon } from '@/components/icons';
import useToggle from '@/data/hook/useToggle';
import Subchapters from './Subchapters';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import { bookType } from '@/models/Book';

type ChaptersProps = {
  chapters: Chapter[];
  activeChapterId?: number;
  activeSubchapterId?: number;
  onReorderChapters: (chapters: Chapter[]) => void;
  onAddChapter: () => void;
  isAddingChapter: boolean;
  readOnly?: boolean;
  onChapterOrSubchapter: (selected: Chapter | Subchapter) => void;
  onAddSubchapter: (chapter: Chapter) => Promise<Subchapter>;
  isAddingSubchapter?: boolean;
  onReorderSubchapter?: (chapterId: number, subchapters: Subchapter[]) => void;
  onUpdateChapter: (chapter: Chapter, deleteChapter?: boolean) => void;
  lessonId: number;
  bookId: number;
  bookType: bookType;
  onDeleteChapter: (chapter: Chapter) => void;
  isUniqueChapter?: boolean;
};

export default function ChaptersService({
  chapters: bookChapters,
  activeChapterId,
  activeSubchapterId,
  readOnly,
  isAddingChapter,
  onReorderChapters,
  onAddChapter,
  onChapterOrSubchapter,
  onAddSubchapter,
  isAddingSubchapter,
  onReorderSubchapter,
  onUpdateChapter,
  onDeleteChapter,
  isUniqueChapter,
  ...props
}: ChaptersProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const [chapters, setChapters] = useState(bookChapters);

  const handleDragEnd: OnDragEndResponder = res => {
    const reordenedResult = dropHandler(res, chapters);
    setChapters(reordenedResult);
    onReorderChapters(reordenedResult);
  };

  const handleDragStart: OnDragStartResponder = res => {
    const chapter = chapters.at(res.source.index);
    if (!chapter) return;
    onChapterOrSubchapter?.(chapter);
  };

  const { isOpen, toggle } = useToggle(true);

  useEffect(() => {
    setChapters(bookChapters);
  }, [bookChapters]);

  return (
    <ModularCard className={{ base: 'w-72 max-h-[25.875rem]' }}>
      <ModularCard.Header
        icon={<StudentBookColorIcon />}
        title={t('content')}
        isOpen={isOpen}
        onOpen={toggle}
        className={{
          base: 'p-0 px-3.5 py-2.5',
          figure: 'bg-secondary-content',
        }}
        hideTooltip
      />
      <ModularCard.Accordion isOpen={isOpen}>
        <ModularCard.Division className={{ base: ' border-secondary/40' }} />
        <DragDropList
          disabled={isAddingChapter || isAddingSubchapter}
          groupId="chapters"
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          className={twJoin(
            'relative overflow-hidden flex flex-col p-3.5 h-full',
            scrollBarYClassName,
          )}
        >
          {chapters.map((chapter, index) => (
            <DragDropItem
              id={'chapter' + chapter.id}
              index={index}
              key={'chapter' + chapter.id}
              disabled={readOnly}
              hideDragIconHandleOnDisabled
              className={{ button: 'top-0 mb-2.5' }}
            >
              <Subchapters
                chapter={chapter}
                selectedChapter={chapter.id === activeChapterId}
                activeSubchapterId={activeSubchapterId}
                onChapterOrSubchapter={onChapterOrSubchapter}
                readOnly={readOnly}
                onAddSubchapter={onAddSubchapter}
                isAddingSubchapter={isAddingSubchapter}
                onReorder={onReorderSubchapter}
                onUpdateChapter={onUpdateChapter}
                onDeleteChapter={onDeleteChapter}
                isUniqueChapter={isUniqueChapter}
                {...props}
              />
            </DragDropItem>
          ))}
        </DragDropList>
      </ModularCard.Accordion>
      <ConditionalRenderer condition={!readOnly}>
        <MainButton
          dataTestId="addChapterButton"
          color="custom"
          className="relative text-primary font-600 gap-1 pb-3.5 px-3.5"
          text={t('add')}
          icon={<PlusIcon />}
          loading={isAddingChapter}
          onClick={onAddChapter}
          disabled={isAddingSubchapter}
        />
      </ConditionalRenderer>
    </ModularCard>
  );
}
